import { inject, Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { DiscussionBase } from '../../../pages/employee-flow/request/base/discussion-base';
import { RequestService } from '../request/request.service';
import {
  AccessKeyResponse,
  WorkflowAccessKeyService,
} from '../workflow-access-key/workflow-access-key.service';
import { EMPLOYEE_ROUTES } from '../../constants/employee.routing.constants';
import { RoleTypes } from '../../constants/role-types.constant';
import { AppStorage } from '../../enums/app-storage';
import { NotificationCheckMode } from '../../enums/notification-check-mode.enum';
import { RequestTypeEnum } from '../../enums/request-type';
import { StepStatusEnum } from '../../enums/step-status';
import { CookieService } from '../cookie/cookie.service';
import { TenantService } from '../tenant/tenant.service';
import {
  Notification,
  NotificationTypeValue,
} from '../../../../ui-components/lib/notification/notifications-type.enum';

@Injectable({
  providedIn: 'root',
})
export class NotificationCheckModeService {
  private readonly router = inject(Router);
  private readonly requestService = inject(RequestService);
  private readonly discussionBase = new DiscussionBase();
  private readonly workflowAccessKeyService = inject(WorkflowAccessKeyService);
  private readonly cookieService = inject(CookieService);
  private readonly tenantService = inject(TenantService);

  navigateToNotification(
    notification: Notification,
    destroy$: Subject<void>
  ): void {
    if (
      this.navigateToProjectFinanceNotification(notification) ||
      this.navigateToBackgroundTaskNotification(notification)
    ) {
      return;
    }

    const queryParams = this.addNavigationQueryParams(notification);

    if (notification.data?.flow_id && notification.data?.step_id) {
      if (
        notification.notification_type.value ===
        NotificationTypeValue.CONTRIBUTOR_ADDED
      ) {
        this.router.navigate([
          EMPLOYEE_ROUTES.MY_INBOX.LIST,
          notification.data.step_id,
          RequestTypeEnum.CONTRIBUTE,
          notification.data.flow_id,
        ]);
      } else {
        this.checkForAuthorization(notification, destroy$, queryParams);
      }
    } else if (notification.data?.flow_id) {
      this.router.navigate(
        [
          EMPLOYEE_ROUTES.MY_INBOX.REQUEST,
          NotificationCheckMode.VIEW,
          notification.data.flow_id,
        ],
        { queryParams }
      );
    } else if (
      (notification.notification_type.value ===
        NotificationTypeValue.DELEGATION_ACTIVATED ||
        notification.notification_type.value ===
          NotificationTypeValue.UPCOMING_DELEGATION) &&
      notification.data?.delegation_id
    ) {
      this.router.navigate([
        EMPLOYEE_ROUTES.ACCOUNT_SETTINGS.DELEGATION_DETAILS,
        notification.data?.delegation_id,
      ]);
    }
  }

  private navigateToProjectFinanceNotification(
    notification: Notification
  ): boolean {
    const { project_id: projectId, record_id: recordId } =
      notification.data ?? {};
    if (!projectId) {
      return false;
    }

    switch (notification.notification_type.value) {
      case NotificationTypeValue.BORROWING_CREATED:
      case NotificationTypeValue.BORROWING_UPDATED:
        if (!recordId) {
          return false;
        }
        this.router.navigate([
          this.tenantService.tenantPath(
            `${EMPLOYEE_ROUTES.PROJECTS.LIST}/${projectId}/${EMPLOYEE_ROUTES.PROJECTS.BORROWINGS.VIEW}/${recordId}`
          ),
        ]);
        return true;
      case NotificationTypeValue.TIME_DEPOSIT_CREATED:
      case NotificationTypeValue.TIME_DEPOSIT_UPDATED:
        this.router.navigate(
          [
            this.tenantService.tenantPath(
              `${EMPLOYEE_ROUTES.PROJECTS.DETAILS}/${projectId}`
            ),
          ],
          {
            queryParams: {
              tab: EMPLOYEE_ROUTES.PROJECTS.TABS.BANK_ACCOUNTS,
              [EMPLOYEE_ROUTES.PROJECTS.SECTION_QUERY_PARAM]:
                EMPLOYEE_ROUTES.PROJECTS.SECTIONS.TIME_DEPOSIT,
            },
          }
        );
        return true;
      default:
        return false;
    }
  }

  /** KUIP-196: an import's received / completed / failed notification opens its result screen. */
  private navigateToBackgroundTaskNotification(
    notification: Notification
  ): boolean {
    const backgroundTaskId = notification.data?.background_task_id;
    const isBackgroundTask = [
      NotificationTypeValue.BACKGROUND_TASK_RECEIVED,
      NotificationTypeValue.BACKGROUND_TASK_COMPLETED,
      NotificationTypeValue.BACKGROUND_TASK_FAILED,
    ].includes(notification.notification_type.value as NotificationTypeValue);

    if (!isBackgroundTask || !backgroundTaskId) {
      return false;
    }

    this.router.navigate([
      this.tenantService.tenantPath(EMPLOYEE_ROUTES.BACKGROUND_TASKS),
      backgroundTaskId,
    ]);

    return true;
  }

  private addNavigationQueryParams(notification: Notification): Params {
    const queryParams: Params = {};
    if (
      notification.notification_type.value ===
        NotificationTypeValue.COMMENT_MENTION ||
      notification.notification_type.value ===
        NotificationTypeValue.COMMENT_REACTION
    ) {
      queryParams[this.discussionBase.DISCUSSION_COMMENT_QUERY_PARAM] = true;
      queryParams[this.discussionBase.DISCUSSION_COMMENT_ID_QUERY_PARAM] =
        notification.data?.comment_id;
    }

    return queryParams;
  }

  checkForAuthorization(
    notification: Notification,
    destroy$: Subject<void>,
    queryParams: Params
  ) {
    this.handleNavigationWithWorkflowAccessKey(
      notification,
      destroy$,
      queryParams
    );
  }

  private handleNavigationWithWorkflowAccessKey(
    notification: Notification,
    destroy$: Subject<void>,
    queryParams: Params
  ) {
    this.workflowAccessKeyService
      .getFlowAuthorizationKey(notification.data.flow_id)
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (res: AccessKeyResponse) => {
          const accessKey = res.data.access_key;
          sessionStorage.setItem(AppStorage.accessKey, accessKey);
          this.checkStepStateAndNavigate(
            notification.data.flow_id,
            notification.data.step_id,
            destroy$,
            queryParams
          );
        },
        error: () => {
          this.router.navigate(
            [
              EMPLOYEE_ROUTES.MY_INBOX.REQUEST,
              NotificationCheckMode.VIEW,
              notification.data.flow_id,
            ],
            {
              queryParams: {
                ...queryParams,
                stepId: notification.data.step_id,
              },
            }
          );
        },
      });
  }

  private checkStepStateAndNavigate(
    flowId: number,
    stepId: number,
    destroy$: Subject<void>,
    queryParams?: Params
  ): void {
    this.requestService
      .getFlowDetails(flowId.toString())
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: response => {
          const mode = this.determineNavigationMode(response.data, stepId);
          if (mode === NotificationCheckMode.ACTION) {
            this.router.navigate(
              [EMPLOYEE_ROUTES.MY_INBOX.LIST, stepId, mode, flowId],
              {
                queryParams,
              }
            );
          } else {
            this.router.navigate(
              [EMPLOYEE_ROUTES.MY_INBOX.REQUEST, mode, flowId],
              {
                queryParams: { ...queryParams, stepId },
              }
            );
          }
        },
        error: () => {
          this.router.navigate(
            [
              EMPLOYEE_ROUTES.MY_INBOX.REQUEST,
              NotificationCheckMode.VIEW,
              flowId,
            ],
            { queryParams: { ...queryParams, stepId } }
          );
        },
      });
  }

  private getCurrentUserId(): number | null {
    const authData = JSON.parse(
      this.cookieService.get(AppStorage.centralUser) || '{}'
    );
    return authData?.id ?? null;
  }

  private determineNavigationMode(
    flowData: any,
    stepId: number
  ): NotificationCheckMode {
    if (!flowData?.positions) {
      return NotificationCheckMode.VIEW;
    }

    for (const position of flowData.positions) {
      for (const step of position.steps) {
        if (step.details.id === stepId) {
          const state = step.details.state.value;

          if (
            state === StepStatusEnum.COMPLETED ||
            state === StepStatusEnum.SKIPPED
          ) {
            return NotificationCheckMode.VIEW;
          }
          if (!step.details.assignees || step.details.assignees.length === 0) {
            return NotificationCheckMode.VIEW;
          }

          const currentUserId = this.getCurrentUserId();
          const currentUserAssignee = step.details.assignees.find(
            (assignee: any) =>
              assignee.type === RoleTypes.USER &&
              assignee.data?.id === currentUserId
          );

          return currentUserAssignee?.finished_at === null
            ? NotificationCheckMode.ACTION
            : NotificationCheckMode.VIEW;
        }
      }
    }

    return NotificationCheckMode.VIEW;
  }
}
